import { CssBaseline, ThemeProvider, createTheme, useMediaQuery } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider as DatePickerLocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ClientComponent } from 'components/guardedComponent/ClientComponent';
import { AppProvider } from 'contexts/AppProvider';
import { MusicPlayerProvider } from 'contexts/MusicPlayerContext';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/pt';
import { useUser } from 'hooks/useUser';
import { DefaultLayout } from 'layouts/DefaultLayout';
import { MinimalLayout } from 'layouts/MinimalLayout';
import { ProtectedRoute } from 'layouts/ProtectedRoute';
import { useMemo } from 'react';
import { BrowserRouter, HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { isElectron } from 'utils/isElectron';
import { Auth } from 'views/Auth';
import { EventDetails } from 'views/EventDetails';
import { EventLiveMode } from 'views/EventLiveMode';
import { EventRequests } from 'views/EventRequests';
import { Events } from 'views/Events';
import { LiveEventRequests } from 'views/LiveEventRequests';
import { MusicLibrary } from 'views/MusicLibrary';
import { Profile } from 'views/Profile';
import { Settings } from 'views/Settings';
import { Spotify } from 'views/Spotify';
import { Subscription } from 'views/Subscription';
import './App.css';
import './i18n';
import { getDesignTokens } from './theme';

export default function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

function AppContent() {
  const { preferences } = useUser();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const colorMode =
    preferences.themeMode === 'system'
      ? prefersDarkMode
        ? 'dark'
        : 'light'
      : preferences.themeMode;

  const theme = useMemo(() => createTheme(getDesignTokens(colorMode)), [colorMode]);
  const Router = isElectron() ? HashRouter : BrowserRouter;
  dayjs.locale(preferences.locale);

  return (
    <DatePickerLocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={preferences.locale}>
      <ThemeProvider theme={theme}>
        <MusicPlayerProvider>
          <CssBaseline />
          <Router>
            <Routes>
              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<DefaultLayout />}>
                  <Route index element={<Navigate to="/events" replace />} />
                  <Route path="/events">
                    <Route index element={<Events />} />
                    <Route path=":eventId" element={<EventDetails />} />
                  </Route>
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/subscription" element={<Subscription />} />
                  <Route path="/settings" element={<Settings />} />

                  <Route
                    path="/musicLibrary"
                    element={
                      <ClientComponent mode="overlay" showDownloadButton fallback={<></>}>
                        <MusicLibrary />
                      </ClientComponent>
                    }
                  />
                </Route>

                <Route path="/events/:eventId/live" element={<EventLiveMode />} />
              </Route>

              {/* Public routes */}
              <Route element={<MinimalLayout />}>
                <Route index path="/" element={<Navigate to="/auth" replace />} />
                <Route path="/auth">
                  <Route index element={<Auth />} />
                  {/* DISABLE LOGIN VIA EMAIL
                      <Route path="register" element={<Auth section="register" />} />
                      <Route path="resetPassword" element={<Auth section="resetPassword" />} />
                      <Route path="verifyEmail" element={<Auth section="verifyEmail" />} />
                       */}
                </Route>
                <Route path="/spotify" element={<Spotify />} />
                <Route path="/:userId/events/:eventId/live-request">
                  <Route index element={<LiveEventRequests />} />
                </Route>
                <Route path="/:userId/events/:eventId/request">
                  <Route index element={<EventRequests />} />
                </Route>
              </Route>
            </Routes>
          </Router>
        </MusicPlayerProvider>
      </ThemeProvider>
    </DatePickerLocalizationProvider>
  );
}
